import { z } from 'zod';
import { CssLengthSchema, FontFamilySchema, HexColorSchema, StyleTokensGrammarSchema } from './element-props.schema';
import { ThemeOptionsSchema } from './theme-options.schema';

// The named design tokens every section binds to — the palette, the type, the corners, the spacing and
// the two buttons — and the subset of them one store has changed. A theme ships the whole set; a store
// carries only what it edited, so a theme's own update still reaches every token the merchant left alone.
// The token vocabulary below is the twin of `ThemeStyles` in `frontend/src/themes/registry.ts`.

export const ThemeColorsSchema = z.object({
  primary: HexColorSchema, primaryHover: HexColorSchema, onPrimary: HexColorSchema, accent: HexColorSchema,
  surface: HexColorSchema, surfaceAlt: HexColorSchema, dark: HexColorSchema, onDark: HexColorSchema,
  heading: HexColorSchema, text: HexColorSchema, textMuted: HexColorSchema, border: HexColorSchema,
  success: HexColorSchema, danger: HexColorSchema,
  // The shop's own parts, optional so every theme written before them still loads: each one a theme
  // leaves out is drawn in the palette colour `themeColor` names beside it.
  card: HexColorSchema.optional(), sale: HexColorSchema.optional(), badgeNew: HexColorSchema.optional(),
  badgeHot: HexColorSchema.optional(), badgeSoldOut: HexColorSchema.optional(), rating: HexColorSchema.optional()
}).strict();

const FontWeightSchema = z.number().int().min(100).max(900);

/**
 * Optional scales, so a merchant can name more than one size. Measured on a real preset page: it held
 * `18px` nineteen times and `16px` seventeen times, and with a single `baseSize` slot only one of the
 * two could ever become a variable — the other stayed a literal in thirty-six places. Optional because
 * every theme shipped before today names none of them and must keep loading unchanged.
 */
export const ThemeFontScaleSchema = z.object({
  xs: CssLengthSchema.optional(), sm: CssLengthSchema.optional(), md: CssLengthSchema.optional(),
  lg: CssLengthSchema.optional(), xl: CssLengthSchema.optional()
}).strict();

export const ThemeWeightScaleSchema = z.object({
  light: FontWeightSchema.optional(), regular: FontWeightSchema.optional(),
  medium: FontWeightSchema.optional(), bold: FontWeightSchema.optional()
}).strict();

export const ThemeLineHeightScaleSchema = z.object({
  tight: z.number().min(0.5).max(3).optional(), normal: z.number().min(0.5).max(3).optional(),
  loose: z.number().min(0.5).max(3).optional()
}).strict();

export const ThemeFontsSchema = z.object({
  heading: FontFamilySchema, body: FontFamilySchema, accent: FontFamilySchema,
  baseSize: CssLengthSchema, headingWeight: FontWeightSchema,
  sizes: ThemeFontScaleSchema.optional(),
  weights: ThemeWeightScaleSchema.optional(),
  lineHeights: ThemeLineHeightScaleSchema.optional()
}).strict();

/** One shadow: `none`, or the offsets-blur-colour string CSS `box-shadow` takes, kept clear of injection. */
const CssShadowSchema = z.string().min(1).max(160).regex(/^[A-Za-z0-9#.,()\/%\s-]+$/, 'Shadows must be a CSS box-shadow value or "none"');

/**
 * The theme's depth system, optional the way the font scales are: sm/md/lg named shadows a card or CTA
 * binds instead of carrying its own string, so a tinted depth system is one token edit. Every theme
 * stored before today names none of them and must keep validating unchanged.
 */
export const ThemeShadowsSchema = z.object({
  sm: CssShadowSchema.optional(), md: CssShadowSchema.optional(), lg: CssShadowSchema.optional()
}).strict();

export const ThemeRadiusSchema = z.object({ sm: CssLengthSchema, md: CssLengthSchema, lg: CssLengthSchema, pill: CssLengthSchema }).strict();
export const ThemeSpacingSchema = z.object({ xs: CssLengthSchema, sm: CssLengthSchema, md: CssLengthSchema, lg: CssLengthSchema, xl: CssLengthSchema }).strict();

/**
 * One button's whole skin, the twin of `ThemeButton` in `frontend/src/themes/registry.ts`.
 * `gradientFrom` is the second stop a gradient button reads; a flat theme repeats `background` there.
 *
 * The five geometry fields exist because the six colour fields were the whole of it, and a skin that
 * says what a button is painted but not how big it is leaves its size to whichever stylesheet gets there
 * first. Measured before they were added: six themes rendered six different font weights and four
 * different border widths, none of them derivable from any token, plus twenty per-section CSS rules that
 * each re-decided a button's padding. Naming them here makes them one answer, and — because
 * `STYLE_TOKENS` is read off this schema's own keys — publishes them to the inspector with no second edit.
 */
export const ThemeButtonSchema = z.object({
  background: HexColorSchema, gradientFrom: HexColorSchema, textColor: HexColorSchema,
  hoverBackground: HexColorSchema, borderColor: HexColorSchema, radius: CssLengthSchema,
  paddingBlock: CssLengthSchema.optional(), paddingInline: CssLengthSchema.optional(),
  fontSize: CssLengthSchema.optional(), fontWeight: FontWeightSchema.optional(),
  borderWidth: CssLengthSchema.optional()
}).strict();

/** The two buttons a theme paints; a CTA names one of them instead of carrying its own hexes. */
export const ThemeButtonsSchema = z.object({ primary: ThemeButtonSchema, secondary: ThemeButtonSchema }).strict();

export const ThemeStylesSchema = z.object({
  colors: ThemeColorsSchema, fonts: ThemeFontsSchema, radius: ThemeRadiusSchema, spacing: ThemeSpacingSchema,
  buttons: ThemeButtonsSchema.optional(), shadows: ThemeShadowsSchema.optional(),
  /** What the shop does — card parts, the cart, search — as Shopify's theme settings groups set it. */
  options: ThemeOptionsSchema.optional()
}).strict();

/** A store's own buttons: either variant, and within it only the tokens it repainted. */
const StoreButtonsSchema = z.object({
  primary: ThemeButtonSchema.partial().optional(),
  secondary: ThemeButtonSchema.partial().optional()
}).strict();

// Runs the section-settings grammar over a store's tokens, so a value refused inside a section — a
// script, a `javascript:` URL, an over-long string — is refused here for exactly the same reason.
function assertTokenGrammar(styles: unknown, ctx: z.RefinementCtx): void {
  const grammar = StyleTokensGrammarSchema.safeParse(styles);
  if (grammar.success) return;
  for (const issue of grammar.error.issues) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
  }
}

/**
 * What one store has changed about its theme's tokens, as `store_themes.config.styles` holds it: every
 * group optional, every token within it optional, and nothing outside the theme's own vocabulary — each
 * group is `.strict()`, so a token another theme names is refused rather than stored as dead JSON.
 */
export const StoreThemeStylesSchema = z.object({
  colors: ThemeColorsSchema.partial().optional(),
  fonts: ThemeFontsSchema.partial().optional(),
  radius: ThemeRadiusSchema.partial().optional(),
  spacing: ThemeSpacingSchema.partial().optional(),
  buttons: StoreButtonsSchema.optional(),
  shadows: ThemeShadowsSchema.optional(),
  options: ThemeOptionsSchema.optional()
}).strict().superRefine(assertTokenGrammar);

export type ThemeColorsDto = z.infer<typeof ThemeColorsSchema>;
export type ThemeFontsDto = z.infer<typeof ThemeFontsSchema>;
export type ThemeButtonDto = z.infer<typeof ThemeButtonSchema>;
export type ThemeButtonsDto = z.infer<typeof ThemeButtonsSchema>;
export type ThemeShadowsDto = z.infer<typeof ThemeShadowsSchema>;
export type ThemeStylesDto = z.infer<typeof ThemeStylesSchema>;
export type StoreThemeStylesDto = z.infer<typeof StoreThemeStylesSchema>;

// Lays a store's buttons over the theme's, variant by variant. A theme that paints no buttons keeps
// none: a half-written button would reach a storefront that derives its buttons from the palette
// instead, and it would derive them from nothing.
function mergeButtons(base: ThemeButtonsDto | undefined, override: StoreThemeStylesDto['buttons']): ThemeButtonsDto | undefined {
  if (!base || !override) return base;
  return {
    primary: { ...base.primary, ...override.primary },
    secondary: { ...base.secondary, ...override.secondary }
  };
}

/**
 * Lays a store's tokens over a theme's — one level per group, the same way a section's settings merge —
 * and answers a whole styles block. A token the store never named keeps the theme's own value, which is
 * what lets a theme update reach a shop that has only repainted its primary colour.
 */
export function mergeThemeStyles(base: ThemeStylesDto, override: StoreThemeStylesDto): ThemeStylesDto {
  const buttons = mergeButtons(base.buttons, override.buttons);
  const shadows = base.shadows || override.shadows ? { ...base.shadows, ...override.shadows } : undefined;
  const options = base.options || override.options ? { ...base.options, ...override.options } : undefined;
  return {
    colors: { ...base.colors, ...override.colors },
    fonts: { ...base.fonts, ...override.fonts },
    radius: { ...base.radius, ...override.radius },
    spacing: { ...base.spacing, ...override.spacing },
    ...(buttons ? { buttons } : {}),
    ...(shadows ? { shadows } : {}),
    ...(options ? { options } : {})
  };
}
